import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { List, Button, Typography, Tag, Avatar, Space, message } from 'antd'
import { UserOutlined, ArrowLeftOutlined, StarOutlined, StarFilled, PushpinOutlined, PushpinFilled, MessageOutlined } from '@ant-design/icons'
import { fragmentApi } from '../api/fragment'
import { FragmentInfo } from '../types'

const { Title } = Typography

export default function MentionList() {
  const navigate = useNavigate()
  const [mentions, setMentions] = useState<FragmentInfo[]>([])
  const [loading, setLoading] = useState(false)

  const loadMentions = async () => {
    setLoading(true)
    try {
      const data = await fragmentApi.getMentionList()
      setMentions(data)
    } finally {
      setLoading(false)
    }
  }

  const handleMarkRead = async (fragmentId: number) => {
    try {
      await fragmentApi.markMentionRead(fragmentId)
      loadMentions()
    } catch (error) {
      message.error('标记失败')
    }
  }

  const handleToggleFavorite = async (fragmentId: number) => {
    try {
      await fragmentApi.toggleFavorite(fragmentId)
      loadMentions()
    } catch (error) {
      message.error('操作失败')
    }
  }

  const handleToggleTop = async (fragmentId: number) => {
    try {
      await fragmentApi.toggleTop(fragmentId)
      loadMentions()
    } catch (error) {
      message.error('操作失败')
    }
  }

  useEffect(() => {
    loadMentions()
  }, [])

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
          >
            返回
          </Button>
        </div>

        <Title level={2}>我的@列表</Title>

        <List
          dataSource={mentions}
          loading={loading}
          renderItem={(fragment) => (
            <List.Item
              key={fragment.id}
              className={`bg-white rounded-lg shadow-sm p-4 mb-3 ${fragment.isTop ? 'border-l-4 border-yellow-500' : ''} ${!fragment.isMentionRead ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}
              actions={[
                !fragment.isMentionRead && (
                  <Button type="primary" onClick={() => handleMarkRead(fragment.id)}>
                    标记已读
                  </Button>
                ),
                <Button
                  type="text"
                  icon={<MessageOutlined />}
                  onClick={() => navigate(`/team/${fragment.teamId}`)}
                >
                  查看对话
                </Button>,
                <Button
                  type="text"
                  icon={fragment.isFavorite ? <StarFilled /> : <StarOutlined />}
                  onClick={() => handleToggleFavorite(fragment.id)}
                />,
                fragment.canTop && (
                  <Button
                    type="text"
                    icon={fragment.isTop ? <PushpinFilled /> : <PushpinOutlined />}
                    onClick={() => handleToggleTop(fragment.id)}
                  />
                ),
              ].filter(Boolean)}
            >
              <List.Item.Meta
                avatar={<Avatar src={fragment.userAvatar} icon={<UserOutlined />} />}
                title={
                  <Space>
                    <span>{fragment.userNickname}</span>
                    {fragment.isAi && <Tag color="purple">AI</Tag>}
                    {!fragment.isMentionRead && <Tag color="blue">新消息</Tag>}
                  </Space>
                }
                description={
                  <div>
                    {fragment.replyToFragment && (
                      <div className="bg-gray-50 border-l-2 border-gray-300 pl-3 py-2 mb-2 rounded-r text-sm">
                        <div className="text-gray-500 mb-1">回复 {fragment.replyToFragment.userNickname}</div>
                        <div className="text-gray-600">
                          {fragment.replyToFragment.isRecalled ? '[已撤回]' : fragment.replyToFragment.content.substring(0, 80)}
                          {fragment.replyToFragment.content.length > 80 && !fragment.replyToFragment.isRecalled ? '...' : ''}
                        </div>
                      </div>
                    )}
                    <p className="text-gray-700 mt-2">{fragment.content}</p>
                    <p className="text-gray-400 text-xs mt-2">{fragment.createTime}</p>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      </div>
    </div>
  )
}
